import { useId } from 'react'
import { useT } from '_metronic/i18n/Metronici18n'
import { BlocoSuspeita } from '../../core/_models'
import { formatarContagem, formatarSegundos } from '../../core/formatacao'
import { AjudaIndicador, montarNotasAjuda } from '../ajuda/AjudaIndicador'
import { TabelaQuebra } from './TabelaQuebra'
import { IconPaths, KTSVG } from '_metronic/helpers'

type Props = {
  dados: BlocoSuspeita
  /**
   * `rotulos.suspeitaNaoSomada` — vai para o `?` junto com as demais notas. A
   * mensagem curta continua visível no título da seção ("nunca somada ao
   * indicador oficial"), que é o que impede a leitura errada do painel.
   */
  notaNaoSomada?: string
  /** `rotulos.coberturaSuspeita` — o que a heurística cobre no fornecedor de vídeo configurado. */
  notaCobertura?: string
}

/**
 * Painel heurístico, em card próprio e separado do indicador oficial. A separação
 * é estrutural, não estética: o número oficial conta o que foi declarado e a
 * heurística olha os logs de presença — somar os dois produziria um total que não
 * existe. Nenhuma expressão deste componente combina campos das duas origens.
 *
 * Aqui `0` é **zero legítimo** — medição feita, nada encontrado —, e por isso vem
 * acompanhado dos pares avaliados, dos pares descartados e da janela usada, em vez
 * do "Sem dados no período" que significa denominador zero em outros cards.
 * Os pares ficam como métricas secundárias ao lado do número; a janela e as notas
 * da API, no `?` do cabeçalho.
 */
export const CardSuspeita = ({ dados, notaNaoSomada, notaCobertura }: Props) => {
  const { t } = useT()
  const idBase = useId()
  const idTitulo = `${idBase}-titulo`

  const janela = t('indicadores.qualidade.suspeita.janela', {
    minimo: formatarSegundos(dados.gapMinimoSegundos),
    maximo: formatarSegundos(dados.gapMaximoSegundos),
  })
  const notas = montarNotasAjuda(idBase, [janela, notaCobertura, notaNaoSomada])
  const titulo = t('indicadores.qualidade.suspeita.atendimentos')

  const reconciliacao = {
    declaradasNoRecorte: dados.declaradasNoRecorte,
    somenteDeclarada: dados.somenteDeclarada,
    somenteSuspeita: dados.somenteSuspeita,
    ambas: dados.ambas,
  }

  return (
    <div
      className='card card-flush h-100 border border-gray-300'
      role='group'
      aria-labelledby={idTitulo}
      aria-describedby={notas.map((nota) => nota.id).join(' ')}
    >
      <div className='card-body d-flex flex-column p-6'>
        <div className='d-flex justify-content-between align-items-start gap-2 mb-3'>
          <h4 id={idTitulo} className='fs-6 fw-semibold text-gray-700 mb-0'>
            {titulo}
          </h4>
          <AjudaIndicador rotulo={t('indicadores.ajuda.rotulo', { titulo })} notas={notas} />
        </div>

        <div className='d-flex flex-wrap align-items-end justify-content-between gap-6'>
          <div>
            {dados.coberturaProfissionalIndisponivel ? (
              <p className='d-flex align-items-start text-gray-900 fs-6 mb-0'>
                <KTSVG path={IconPaths.CircleSlash} className='svg-icon-em me-2 fs-5' ariaHidden />
                <span>{t('indicadores.qualidade.suspeita.cobertura_parcial')}</span>
              </p>
            ) : (
              <>
                <p className='fs-2hx fw-bold text-gray-900 mb-1'>
                  {formatarContagem(dados.atendimentosComSuspeita)}
                </p>
                {dados.atendimentosComSuspeita === 0 && (
                  <p className='fs-7 text-gray-900 mb-0'>
                    {t('indicadores.qualidade.suspeita.zero')}
                  </p>
                )}
              </>
            )}
          </div>

          <dl className='d-flex gap-6 mb-0'>
            <div>
              <dt className='fs-8 fw-normal text-gray-700'>
                {t('indicadores.qualidade.suspeita.pares_na_janela')}
              </dt>
              <dd className='fs-5 fw-semibold text-gray-900 mb-0'>
                {formatarContagem(dados.paresNaJanela)}
              </dd>
            </div>
            <div>
              <dt className='fs-8 fw-normal text-gray-700'>
                {t('indicadores.qualidade.suspeita.pares_ignorados')}
              </dt>
              <dd className='fs-5 fw-semibold text-gray-900 mb-0'>
                {formatarContagem(dados.paresIgnoradosForaDaJanela)}
              </dd>
            </div>
          </dl>
        </div>

        <TabelaQuebra
          titulo={t('indicadores.qualidade.suspeita.reconciliacao_titulo')}
          colunaCategoria={t('indicadores.qualidade.suspeita.coluna_categoria')}
          colunaValor={t('indicadores.qualidade.suspeita.coluna_valor')}
          dados={reconciliacao}
          prefixoRotulo='indicadores.qualidade.suspeita.reconciliacao'
          textoVazio={t('indicadores.qualidade.suspeita.reconciliacao_vazia')}
        />
      </div>
    </div>
  )
}
